import { Injectable } from "@angular/core";
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from "@angular/common/http";
import { Observable, catchError, from, lastValueFrom, throwError } from "rxjs";
import { TokenService } from "@core/services/token.service";
import { ActiveNamespaceService } from "@core/services/namespace/active-namespace.service";
import { CustomHttpRequest } from "@core/models/requests.interface";
import { ActiveInstanceService } from "@core/services/instance/active-instance.service";
import { LanguageService } from "@core/services/language.service";
@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  constructor(
    private tokenService: TokenService,
    private activeNamespaceService: ActiveNamespaceService,
    private activeInstanceService: ActiveInstanceService,
    private languageService: LanguageService,
  ) {}

  intercept(request: HttpRequest<CustomHttpRequest>, next: HttpHandler): Observable<HttpEvent<CustomHttpRequest>> {
    if (!navigator.onLine) {
      return throwError(() => new Error("No internet connection"));
    }

    return from(this.handleAccess(request, next)).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  setAuthToken(request: HttpRequest<CustomHttpRequest>) {
    const token = this.tokenService.getToken();
    const withAuth = request.headers.get("With-Auth") === "true";
    if (withAuth) {
      const headers = request.headers.delete("With-Auth");
      request = request.clone({ headers });
    }
    return request.clone({
      setHeaders: {
        Authorization: "Bearer " + token,
      },
    });
  }

  setSelectedInstanceId(request: HttpRequest<CustomHttpRequest>) {
    const instanceId = this.activeInstanceService.getInstance()?.id;
    const exceptions = ["/instance"];
    const urlHasException = exceptions.some((item) => {
      return request.url.includes(item);
    });

    if (!instanceId || urlHasException) return request;

    return request.clone({
      setHeaders: {
        "Neuron-Selected-Instance": instanceId,
      },
    });
  }

  setActiveNamespaceId(request: HttpRequest<CustomHttpRequest>): HttpRequest<CustomHttpRequest> {
    const namespaceId: string | undefined = this.activeNamespaceService.getNamespaceId();

    if (!namespaceId) {
      return request;
    }

    return request.clone({
      setHeaders: {
        "Neuron-Selected-Namespace": namespaceId,
      },
    });
  }

  private setLocaleCode(request: HttpRequest<CustomHttpRequest>): HttpRequest<CustomHttpRequest> {
    const languageCode = this.languageService.getLocale();
    if (!languageCode) {
      return request;
    }

    return request.clone({
      setHeaders: {
        "Neuron-Locale-Code": languageCode,
        "Accept-Language": languageCode,
      },
    });
  }

  async handleTokenRefresh(request: HttpRequest<CustomHttpRequest>, next: HttpHandler): Promise<HttpEvent<CustomHttpRequest>> {
    const token = await this.tokenService.refreshToken();
    if (token) {
      this.tokenService.setToken(token);
    }
    request = this.setAuthToken(request);
    return await lastValueFrom(next.handle(request));
  }

  private async continueWithoutAuth(request: HttpRequest<CustomHttpRequest>, next: HttpHandler): Promise<HttpEvent<CustomHttpRequest>> {
    const headers = request.headers.delete("Without-Auth");

    request = request.clone({ headers });
    return await lastValueFrom(next.handle(request));
  }

  private async handleAccess(request: HttpRequest<CustomHttpRequest>, next: HttpHandler): Promise<HttpEvent<CustomHttpRequest>> {
    const withoutAuth = request.headers.get("Without-Auth") === "true";
    const isAssetRequest = request.url.includes("assets/");
    const isAccessible = this.isAccessibleUrl(request);
    
    if (withoutAuth || isAccessible || isAssetRequest) {
      return this.continueWithoutAuth(request, next);
    }
    
    request = this.setSelectedInstanceId(request);
    request = this.setActiveNamespaceId(request);
    request = this.setLocaleCode(request);
    
    const isTokenExpired = await this.tokenService.isTokenExpired();

    if (isTokenExpired) {
      return this.handleTokenRefresh(request, next);
    }
    else {
      return lastValueFrom(next.handle(this.setAuthToken(request)));
    }
  }

  private isAccessibleUrl(request: HttpRequest<CustomHttpRequest>): boolean {
    const url = request.url;
    const location = window.location.href;
    
    return (
      (location.includes("register") && (url.includes("/admin/smb/users2/create") || url.includes("/admin/terms-of-service/latest"))) ||
      (location.includes("login") && !url.includes("/instances") && !url.includes("/autocomplete") && !url.includes("/geocode")) ||
      location.includes("forgot-password") ||
      location.includes("activate-account") ||
      url.includes("/admin/social/token") ||
      url.startsWith("./")
    );
  }

}
